"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  PrintButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* HMRC Construction Industry Scheme (CIS) payment maths — verified against
   gov.uk CIS contractor guidance and the VAT domestic reverse charge guidance
   on 6 June 2026:

   1. The CIS deduction applies to the LABOUR element only. From the gross
      amount the contractor first takes off the cost of qualifying materials
      the subcontractor actually paid for (materials, consumables, fuel — other
      than for travelling — and plant hire). VAT is also excluded.
        gov.uk/what-you-must-do-as-a-cis-contractor/make-deductions-and-pay-subcontractors
   2. Deduction rates: gross payment status 0%, registered (verified) 20%,
      unregistered / not verified 30%. Applied to the net labour figure.
   3. VAT domestic reverse charge: where both parties are CIS- and VAT-
      registered and the customer is not an end user, the subcontractor does
      NOT charge VAT — the contractor accounts for it on its own VAT return.
      Without reverse charge, a VAT-registered subcontractor adds VAT on top;
      the CIS deduction is still taken from the net labour, never from the VAT.
        gov.uk/guidance/vat-domestic-reverse-charge-for-building-and-construction-services */

type DeductionStatus = "gross" | "registered" | "unregistered";

const RATES: Record<DeductionStatus, number> = {
  gross: 0,
  registered: 0.2,
  unregistered: 0.3,
};

const STATUS_LABEL: Record<DeductionStatus, string> = {
  gross: "Gross payment status (0%)",
  registered: "Registered — verified (20%)",
  unregistered: "Unregistered / not verified (30%)",
};

const VAT_RATE = 0.2; // standard rate

const gbp = (n: number) =>
  n.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2, maximumFractionDigits: 2 });

function parseAmount(v: string): number {
  const n = parseFloat(v);
  return isFinite(n) && n > 0 ? n : 0;
}

export function CisTaxCalculator() {
  const [labour, setLabour] = useState("");
  const [materials, setMaterials] = useState("");
  const [status, setStatus] = useState<DeductionStatus>("registered");
  const [vatRegistered, setVatRegistered] = useState(true);
  const [reverseCharge, setReverseCharge] = useState(true);
  const [subcontractor, setSubcontractor] = useState("");
  const [verificationRef, setVerificationRef] = useState("");
  const resultTracked = useRef(false);

  const computed = useMemo(() => {
    const labourAmt = parseAmount(labour);
    const materialsAmt = parseAmount(materials);
    if (labourAmt <= 0 && materialsAmt <= 0) return null;

    const rate = RATES[status];
    // CIS deduction is on the labour element only — never on materials or VAT.
    const deduction = labourAmt * rate;
    const netSubtotal = labourAmt + materialsAmt;

    // VAT line. Only a VAT-registered subcontractor that is NOT under the
    // reverse charge actually adds VAT to the invoice. Under reverse charge,
    // or for a non-VAT-registered subcontractor, no VAT is added to the bill.
    const vatApplies = vatRegistered && !reverseCharge;
    const vat = vatApplies ? netSubtotal * VAT_RATE : 0;

    // Amount the contractor pays the subcontractor = labour + materials + VAT
    // (if any) minus the CIS deduction.
    const paidToSub = netSubtotal + vat - deduction;

    let vatLine: string;
    if (!vatRegistered) {
      vatLine = "Subcontractor is not VAT-registered — no VAT on this invoice.";
    } else if (reverseCharge) {
      vatLine =
        "VAT domestic reverse charge applies — the subcontractor does not charge VAT. The contractor accounts for the VAT on its own VAT return (and may reclaim it as input tax under the normal rules).";
    } else {
      vatLine = `VAT charged on top at 20% (${gbp(vat)}). The CIS deduction is still taken from the net labour, not the VAT.`;
    }

    return {
      labourAmt,
      materialsAmt,
      netSubtotal,
      rate,
      deduction,
      vatApplies,
      vat,
      paidToSub,
      vatLine,
    };
  }, [labour, materials, status, vatRegistered, reverseCharge]);

  useEffect(() => {
    if (computed && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("cis-tax-calculator", { rate: RATES[status] });
    }
  }, [computed, status]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Invoice details</h2>
        <p className="mt-1 text-sm text-muted">
          Enter the amounts <strong>before VAT</strong>. The CIS deduction is worked out on the labour element only —
          materials, plant hire and fuel are taken off first.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field label="Labour (£)" hint="The work element of the invoice, excluding VAT.">
            <NumberInput
              min={0}
              step={0.01}
              value={labour}
              onChange={(e) => setLabour(e.target.value)}
              placeholder="e.g. 2000"
            />
          </Field>
          <Field
            label="Materials (£)"
            hint="Materials, consumables, plant hire and fuel the subcontractor actually paid for."
          >
            <NumberInput
              min={0}
              step={0.01}
              value={materials}
              onChange={(e) => setMaterials(e.target.value)}
              placeholder="e.g. 500"
            />
          </Field>

          <Field label="Deduction status" className="sm:col-span-2" hint="Set by HMRC when the contractor verifies the subcontractor.">
            <SelectInput value={status} onChange={(e) => setStatus(e.target.value as DeductionStatus)}>
              <option value="gross">{STATUS_LABEL.gross}</option>
              <option value="registered">{STATUS_LABEL.registered}</option>
              <option value="unregistered">{STATUS_LABEL.unregistered}</option>
            </SelectInput>
          </Field>

          <Field label="Subcontractor VAT-registered?">
            <SelectInput
              value={vatRegistered ? "yes" : "no"}
              onChange={(e) => {
                const yes = e.target.value === "yes";
                setVatRegistered(yes);
                if (!yes) setReverseCharge(false); // no VAT registration -> reverse charge can't apply
              }}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </SelectInput>
          </Field>

          <Field
            label="VAT reverse charge applies?"
            hint="Defaults to yes when both parties are CIS- and VAT-registered and the customer isn't an end user."
          >
            <SelectInput
              value={reverseCharge ? "yes" : "no"}
              onChange={(e) => setReverseCharge(e.target.value === "yes")}
              disabled={!vatRegistered}
            >
              <option value="yes">Yes — reverse charge</option>
              <option value="no">No — VAT added on top</option>
            </SelectInput>
          </Field>

          <Field label="Subcontractor / job reference">
            <TextInput
              value={subcontractor}
              onChange={(e) => setSubcontractor(e.target.value)}
              placeholder="Optional — shown on the printed statement"
            />
          </Field>

          <Field
            label="Contractor verification reference"
            hint="The HMRC verification number for this subcontractor. The 30% rate applies until they are verified."
          >
            <TextInput
              value={verificationRef}
              onChange={(e) => setVerificationRef(e.target.value)}
              placeholder="Optional — e.g. V1234567890"
            />
          </Field>
        </div>

        <p className="mt-4 rounded-lg bg-mist px-4 py-3 text-[0.82rem] leading-relaxed text-muted">
          The <strong>VAT domestic reverse charge</strong> applies to most construction services between two
          VAT-registered, CIS-registered businesses where the customer is <em>not</em> the end user. When it applies,
          the subcontractor leaves VAT off the invoice and the contractor accounts for it. If you&apos;re billing the
          end user (e.g. the building owner), choose &ldquo;No&rdquo; and VAT is added as normal.
        </p>
      </div>

      {/* Result + printable statement */}
      <div className="space-y-4">
        {computed ? (
          <>
            <div className="print-area card shadow-soft p-6" id="cis-statement">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                CIS payment &amp; deduction statement
              </div>
              <h2 className="mt-1 text-lg">
                {subcontractor ? `${subcontractor} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>
              {verificationRef.trim() && (
                <p className="mt-0.5 text-[0.82rem] text-muted">
                  Verification reference: <span className="font-semibold text-ink">{verificationRef.trim()}</span>
                </p>
              )}

              {/* Neutral banner — figures summary, not a risk verdict. */}
              <div className="mt-4 rounded-xl border border-line-2 bg-mist px-5 py-4">
                <div className="font-display text-lg font-bold text-ink">
                  Contractor pays {gbp(computed.paidToSub)}
                </div>
                <div className="mt-1 text-[0.92rem] leading-relaxed text-body">
                  CIS deduction of {gbp(computed.deduction)} ({Math.round(computed.rate * 100)}% of labour) is paid to
                  HMRC, not to the subcontractor.
                </div>
              </div>

              <table className="mt-4 w-full text-sm">
                <tbody>
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-body">Labour</td>
                    <td className="py-2 text-right text-ink">{gbp(computed.labourAmt)}</td>
                  </tr>
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-body">Materials (excluded from deduction)</td>
                    <td className="py-2 text-right text-ink">{gbp(computed.materialsAmt)}</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-2 pr-2 font-semibold text-ink">Subtotal (net of VAT)</td>
                    <td className="py-2 text-right font-semibold text-ink">{gbp(computed.netSubtotal)}</td>
                  </tr>
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-body">
                      CIS deduction ({Math.round(computed.rate * 100)}% on labour)
                    </td>
                    <td className="py-2 text-right text-ink">
                      {computed.deduction > 0 ? `– ${gbp(computed.deduction)}` : gbp(0)}
                    </td>
                  </tr>
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-body">
                      VAT {computed.vatApplies ? "(added at 20%)" : computed.rate === 0 ? "" : "(reverse charge / none)"}
                    </td>
                    <td className="py-2 text-right text-ink">
                      {computed.vatApplies ? `+ ${gbp(computed.vat)}` : gbp(0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-2 font-semibold text-ink">Paid to subcontractor</td>
                    <td className="py-2 text-right font-display text-lg font-bold text-ink">
                      {gbp(computed.paidToSub)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="mt-4 grid grid-cols-2 gap-2 text-center text-[0.78rem]">
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{gbp(computed.paidToSub)}</div>
                  <div className="text-muted">to subcontractor</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{gbp(computed.deduction)}</div>
                  <div className="text-muted">CIS to HMRC</div>
                </div>
              </div>

              <p className="mt-4 rounded-lg border border-line bg-emerald-tint px-4 py-3 text-[0.82rem] leading-relaxed text-emerald-deep">
                The CIS deduction counts towards the subcontractor&apos;s own tax and National Insurance — keep this CIS
                statement to claim it back.
              </p>

              <p className="mt-3 text-[0.8rem] leading-relaxed text-muted">{computed.vatLine}</p>

              {status === "unregistered" && (
                <p className="mt-3 text-[0.8rem] leading-relaxed text-muted">
                  The 30% higher rate applies until the contractor verifies the subcontractor with HMRC. Once
                  verified, most subcontractors move to the 20% standard rate.
                </p>
              )}

              <p className="mt-3 text-[0.78rem] leading-relaxed text-muted">
                Rates verified for the <strong className="text-ink">2026/27 tax year</strong> (verified 6 June 2026).
                The 0% / 20% / 30% split is set by deduction status, not by tax year.
              </p>

              <DataReviewedNote date="6 June 2026">
                Rates and reverse-charge rules verified against gov.uk CIS contractor guidance. Tax rules
                change — confirm at gov.uk before relying on the figures.
              </DataReviewedNote>

              <PrintFooter toolName="CIS tax calculator" />
            </div>
            <div className="flex justify-end">
              <PrintButton tool="cis-tax-calculator" label="Print / save statement as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Enter the labour and materials amounts to see the CIS deduction, the VAT treatment, what the contractor pays
            the subcontractor, and what gets reported to HMRC.
          </div>
        )}
      </div>
    </div>
  );
}
